import React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError, formatRupiah } from "@/lib/api";
import { Wallet, Plus, Minus, X, Lock, LogOut, Shield, AlertTriangle, ChevronDown, ChevronUp, CreditCard, Trash2, Search, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";

const PER_PAGE = 10;

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adjust, setAdjust] = useState({ amount: "", note: "", mode: "tambah" });
  const [actionModal, setActionModal] = useState(null);
  const [expandedReferrals, setExpandedReferrals] = useState({});
  const [referralData, setReferralData] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const load = async () => {
    try {
      const [users, adjustments] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/balance-adjustments"),
      ]);
      setItems(users.data);
      setLogs(adjustments.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((u) => {
    const q = search.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.phone?.includes(q) || u.referral_code?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageData = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  const toggleReferrals = async (userId) => {
    if (expandedReferrals[userId]) {
      setExpandedReferrals({ ...expandedReferrals, [userId]: false });
      return;
    }
    try {
      const res = await api.get(`/admin/users/${userId}/referrals`);
      setReferralData({ ...referralData, [userId]: res.data });
      setExpandedReferrals({ ...expandedReferrals, [userId]: true });
    } catch {}
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    const finalAmount = adjust.mode === "kurangi"
      ? -Math.abs(Number(adjust.amount))
      : Math.abs(Number(adjust.amount));
    try {
      await api.post(`/admin/users/${editing.id}/balance`, { amount: finalAmount, note: adjust.note });
      toast.success("Saldo diperbarui");
      setEditing(null);
      setAdjust({ amount: "", note: "", mode: "tambah" });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const previewBalance = () => {
    const amt = Math.abs(Number(adjust.amount || 0));
    return adjust.mode === "kurangi" ? (editing?.balance || 0) - amt : (editing?.balance || 0) + amt;
  };

  const handleStatus = async (userId, status) => {
    try {
      await api.post(`/admin/users/${userId}/status`, { status });
      toast.success(`Status akun diperbarui: ${status}`);
      setActionModal(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="admin-users">
      <div className="label-mono text-zinc-500">// MEMBERS</div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Pengguna</h1>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-black bg-white">
          <div className="p-3 border-b border-black flex flex-wrap justify-between items-center gap-2">
            <div className="flex items-center gap-3">
              <div className="label-mono text-zinc-500">{filtered.length} pengguna</div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Cari nama / HP / kode referral..." className="input-brutal pl-8 py-1.5 text-xs w-56" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-zinc-500">{page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, filtered.length)} dari {filtered.length}</span>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="font-mono text-[10px]">{page + 1}/{totalPages || 1}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {pageData.length === 0 ? (
            <div className="p-8 text-center font-mono text-xs text-zinc-500">
              {search ? `// tidak ada pengguna dengan kata "${search}"` : "// belum ada pengguna"}
            </div>
          ) : (
            pageData.map((u) => {
              const referrer = u.referred_by ? items.find(x => x.id === u.referred_by) : null;
              const referralCount = items.filter(x => x.referred_by === u.id).length;
              const isExpanded = expandedReferrals[u.id];
              const referrals = referralData[u.id] || [];

              return (
                <div key={u.id} className={`border-b border-black p-4 ${u.status === "frozen" ? "bg-blue-50" : u.status === "restricted" ? "bg-yellow-50" : ""}`}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <div className="label-mono text-zinc-400 mb-1">// NAMA</div>
                      <div className="font-mono font-bold text-sm flex items-center gap-1 flex-wrap">
                        {u.name}
                        {u.role === "admin" && <span className="badge-status bg-[#FFD600] text-black">ADMIN</span>}
                        {u.status === "frozen" && <span className="badge-status bg-blue-500 text-white">FROZEN</span>}
                        {u.status === "restricted" && <span className="badge-status bg-yellow-500 text-black">RESTRICTED</span>}
                      </div>
                      {referrer && (
                        <div className="font-mono text-[10px] text-zinc-500 mt-1">
                          Direferral oleh: <b className="text-black">{referrer.name}</b>
                        </div>
                      )}
                      <div className="font-mono text-[10px] text-zinc-400 mt-0.5">IP: {u.last_ip || "—"}</div>
                    </div>

                    <div>
                      <div className="label-mono text-zinc-400 mb-1">// PHONE</div>
                      <div className="font-mono text-xs">{u.phone}</div>
                      <div className="font-mono text-[10px] text-zinc-500 mt-1">
                        Komisi: <b className="text-[#00C853]">{formatRupiah(u.total_commission || 0)}</b>
                      </div>
                    </div>

                    <div>
                      <div className="label-mono text-zinc-400 mb-1">// REF CODE</div>
                      <div className="font-mono text-xs font-bold tracking-widest">{u.referral_code}</div>
                      <button onClick={() => toggleReferrals(u.id)}
                        className="flex items-center gap-1 font-mono text-[10px] text-[#FF6A00] mt-1 hover:underline">
                        {referralCount} orang pakai kode ini
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                      {isExpanded && (
                        <div className="mt-2 space-y-1">
                          {referrals.length === 0 ? (
                            <div className="font-mono text-[10px] text-zinc-400">// belum ada</div>
                          ) : (
                            referrals.map(r => (
                              <div key={r.id} className="font-mono text-[10px] border border-zinc-200 px-2 py-1">
                                <b>{r.name}</b> · {r.phone}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="label-mono text-zinc-400 mb-1">// SALDO</div>
                      <div className="font-mono font-bold text-sm">{formatRupiah(u.balance)}</div>
                      {u.debt > 0 && (
                        <div className="font-mono text-[10px] text-[#FF3333] mt-0.5">Kekurangan: {formatRupiah(u.debt)}</div>
                      )}
                      <div className="label-mono text-zinc-400 mt-3 mb-1">// AKSI</div>
                      <div className="flex flex-wrap gap-1">
                        <button onClick={() => { setEditing(u); setAdjust({ amount: "", note: "", mode: "tambah" }); }}
                          className="px-2 py-1 border border-black hover:bg-black hover:text-white font-mono text-[10px] flex items-center gap-1">
                          <Wallet className="w-3 h-3" /> Saldo
                        </button>
                        <button onClick={() => setActionModal({ type: "password", user: u })}
                          className="px-2 py-1 border border-black hover:bg-[#FF6A00] hover:text-white font-mono text-[10px] flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Password
                        </button>
                        <button onClick={() => setActionModal({ type: "bank", user: u })}
                          className="px-2 py-1 border border-black hover:bg-[#00C853] hover:text-white font-mono text-[10px] flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Rekening
                        </button>
                        <button onClick={() => setActionModal({ type: "freeze", user: u })}
                          className="px-2 py-1 border border-black hover:bg-blue-500 hover:text-white font-mono text-[10px] flex items-center gap-1">
                          <Lock className="w-3 h-3" /> {u.status === "frozen" ? "Aktifkan" : "Bekukan"}
                        </button>
                        <button onClick={() => setActionModal({ type: "restrict", user: u })}
                          className="px-2 py-1 border border-black hover:bg-yellow-500 hover:text-black font-mono text-[10px] flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Batasi
                        </button>
                        <button onClick={() => setActionModal({ type: "logout", user: u })}
                          className="px-2 py-1 border border-black hover:bg-[#FF3333] hover:text-white font-mono text-[10px] flex items-center gap-1">
                          <LogOut className="w-3 h-3" /> Keluarkan
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="border border-black bg-white">
          <div className="p-4 border-b border-black label-mono">// SALDO LOG</div>
          {logs.length === 0 ? (
            <div className="p-6 text-center font-mono text-xs text-zinc-500">// belum ada</div>
          ) : (
            logs.slice(0, 30).map((l) => (
              <div key={l.id} className="p-3 border-b border-zinc-200">
                <div className="flex justify-between">
                  <span className="font-mono text-xs font-bold">{l.user_name}</span>
                  <span className={`font-mono text-xs font-bold ${l.amount >= 0 ? "text-[#00C853]" : "text-[#FF3333]"}`}>
                    {l.amount >= 0 ? "+" : ""}{formatRupiah(l.amount)}
                  </span>
                </div>
                {l.note && <div className="font-mono text-[10px] text-zinc-600 mt-0.5">"{l.note}"</div>}
                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                  by {l.by_admin_name} · {new Date(l.created_at).toLocaleString("id-ID")}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 z-50" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitAdjust} className="bg-white border border-black w-full max-w-md">
            <div className="p-4 border-b border-black flex justify-between items-center bg-black text-white">
              <div>
                <div className="label-mono text-yellow-400">// ADJUST BALANCE</div>
                <div className="font-display font-black uppercase">{editing.name}</div>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-2 border border-white hover:bg-white hover:text-black">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <div className="label-mono text-zinc-500 mb-1">Saldo saat ini</div>
              <div className="font-mono text-xl font-bold mb-4">{formatRupiah(editing.balance)}</div>
              <label className="label-mono block mb-2">Mode</label>
              <div className="flex gap-2 mb-3">
                <button type="button" onClick={() => setAdjust({ ...adjust, mode: "tambah" })}
                  className={`flex-1 px-3 py-2 border border-black flex items-center justify-center gap-1 font-mono text-xs font-bold uppercase ${adjust.mode === "tambah" ? "bg-[#00C853] text-white" : "hover:bg-zinc-100"}`}>
                  <Plus className="w-3 h-3" /> Tambah
                </button>
                <button type="button" onClick={() => setAdjust({ ...adjust, mode: "kurangi" })}
                  className={`flex-1 px-3 py-2 border border-black flex items-center justify-center gap-1 font-mono text-xs font-bold uppercase ${adjust.mode === "kurangi" ? "bg-[#FF3333] text-white" : "hover:bg-zinc-100"}`}>
                  <Minus className="w-3 h-3" /> Kurangi
                </button>
              </div>
              <label className="label-mono block mb-2">Jumlah ($)</label>
              <input type="number" step={0.01} min={0} value={adjust.amount}
                onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })}
                className="input-brutal mb-3" placeholder="Masukkan jumlah..." required />
              <label className="label-mono block mb-2">Catatan</label>
              <input value={adjust.note} onChange={(e) => setAdjust({ ...adjust, note: e.target.value })}
                className="input-brutal mb-4" placeholder="Top up manual / koreksi / dll" />
              <div className={`font-mono text-xs mb-4 p-2 border ${adjust.mode === "kurangi" ? "border-[#FF3333] bg-red-50 text-[#FF3333]" : "border-[#00C853] bg-green-50 text-[#00C853]"}`}>
                Saldo baru: <b>{formatRupiah(previewBalance())}</b>
                <span className="ml-2">({adjust.mode === "kurangi" ? "Pengurangan" : "Penambahan"})</span>
              </div>
              <button className="btn-primary w-full" type="submit">Simpan Penyesuaian</button>
            </div>
          </form>
        </div>
      )}

      {actionModal && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 z-50" onClick={() => setActionModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-black w-full max-w-md">
            <div className="p-4 border-b border-black flex justify-between items-center bg-black text-white">
              <div>
                <div className="label-mono text-yellow-400">
                  {actionModal.type === "password" && "// GANTI PASSWORD"}
                  {actionModal.type === "bank" && "// REKENING BANK"}
                  {actionModal.type === "freeze" && "// BEKUKAN / AKTIFKAN AKUN"}
                  {actionModal.type === "restrict" && "// BATASI AKSES MASUK"}
                  {actionModal.type === "logout" && "// KELUARKAN DARI AKUN"}
                </div>
                <div className="font-display font-black uppercase">{actionModal.user.name}</div>
              </div>
              <button onClick={() => setActionModal(null)} className="p-2 border border-white hover:bg-white hover:text-black">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              {actionModal.type === "password" && <PasswordChangeForm user={actionModal.user} onClose={() => { setActionModal(null); load(); }} />}
              {actionModal.type === "bank" && <BankAccountsForm user={actionModal.user} onClose={() => setActionModal(null)} />}
              {actionModal.type === "freeze" && (
                <div>
                  <p className="font-mono text-sm mb-4">
                    {actionModal.user.status === "frozen" ? `Aktifkan kembali akun ${actionModal.user.name}?` : `Bekukan akun ${actionModal.user.name}? Pengguna tidak bisa login.`}
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => handleStatus(actionModal.user.id, actionModal.user.status === "frozen" ? "active" : "frozen")}
                      className={`flex-1 btn-primary ${actionModal.user.status === "frozen" ? "bg-[#00C853] border-[#00C853]" : "bg-blue-500 border-blue-500"}`}>
                      {actionModal.user.status === "frozen" ? "Aktifkan" : "Bekukan"}
                    </button>
                    <button onClick={() => setActionModal(null)} className="flex-1 btn-secondary">Batal</button>
                  </div>
                </div>
              )}
              {actionModal.type === "restrict" && (
                <div>
                  <p className="font-mono text-sm mb-2">Pilih status untuk akun <b>{actionModal.user.name}</b>:</p>
                  <div className="space-y-2 mb-4">
                    <button onClick={() => handleStatus(actionModal.user.id, "restricted")}
                      className="w-full px-3 py-2 border border-yellow-500 bg-yellow-50 font-mono text-xs font-bold uppercase text-left hover:bg-yellow-500 hover:text-white">
                      ⚠ PEMBATASAN AKSES MASUK
                    </button>
                    <button onClick={() => handleStatus(actionModal.user.id, "active")}
                      className="w-full px-3 py-2 border border-[#00C853] bg-green-50 font-mono text-xs font-bold uppercase text-left hover:bg-[#00C853] hover:text-white">
                      ✓ AKTIF NORMAL
                    </button>
                  </div>
                  <button onClick={() => setActionModal(null)} className="w-full btn-secondary">Batal</button>
                </div>
              )}
              {actionModal.type === "logout" && (
                <div>
                  <p className="font-mono text-sm mb-4">Keluarkan <b>{actionModal.user.name}</b> dari semua sesi aktif?</p>
                  <div className="flex gap-2">
                    <button onClick={() => { toast.success("Pengguna dikeluarkan"); setActionModal(null); }} className="flex-1 btn-primary bg-[#FF3333] border-[#FF3333]">Keluarkan</button>
                    <button onClick={() => setActionModal(null)} className="flex-1 btn-secondary">Batal</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PasswordChangeForm({ user, onClose }) {
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: newPassword });
      toast.success("Password berhasil diganti!");
      onClose();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit}>
      <p className="font-mono text-sm mb-4">Password baru untuk <b>{user.name}</b>:</p>
      <label className="label-mono block mb-2">Password Baru</label>
      <div className="relative mb-4">
        <input type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
          className="input-brutal w-full pr-12" placeholder="Min. 6 karakter" required minLength={6} />
        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black">
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={loading} className="flex-1 btn-primary">{loading ? "// SAVING..." : "Simpan Password"}</button>
        <button type="button" onClick={onClose} className="flex-1 btn-secondary">Batal</button>
      </div>
    </form>
  );
}

function BankAccountsForm({ user, onClose }) {
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({ bank_name: "", account_no: "", account_name: "", label: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/admin/users/${user.id}/bank-accounts`);
      setBanks(res.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/bank-accounts`, form);
      toast.success("Rekening ditambahkan!");
      setForm({ bank_name: "", account_no: "", account_name: "", label: "" });
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const remove = async (bankId) => {
    if (!window.confirm("Hapus rekening ini?")) return;
    try {
      await api.delete(`/admin/users/${user.id}/bank-accounts/${bankId}`);
      toast.success("Rekening dihapus");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div>
      <p className="font-mono text-sm mb-3">Rekening bank milik <b>{user.name}</b>:</p>
      {banks.length === 0 ? (
        <div className="p-4 border border-dashed border-zinc-300 text-center font-mono text-xs text-zinc-500 mb-4">// Belum ada rekening</div>
      ) : (
        <div className="space-y-2 mb-4">
          {banks.map(b => (
            <div key={b.id} className="border border-black p-3 flex justify-between items-center">
              <div>
                <div className="font-mono text-xs font-bold">{b.bank_name} · {b.account_no}</div>
                <div className="font-mono text-[10px] text-zinc-500">{b.account_name}{b.label && ` · ${b.label}`}</div>
              </div>
              <button onClick={() => remove(b.id)} className="p-1.5 border border-black hover:bg-[#FF3333] hover:text-white">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="border-t border-zinc-200 pt-4 space-y-2">
        <div className="label-mono text-zinc-500 mb-2">// TAMBAH REKENING</div>
        <input placeholder="Bank / E-wallet (contoh: BCA, Dana)" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="input-brutal" required />
        <input placeholder="No. Rekening / No. Akun" value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} className="input-brutal" required />
        <input placeholder="Nama Pemilik Rekening" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} className="input-brutal" required />
        <input placeholder="Label (opsional, contoh: Utama)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input-brutal" />
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={loading} className="flex-1 btn-primary">{loading ? "// SAVING..." : "+ Tambah Rekening"}</button>
          <button type="button" onClick={onClose} className="flex-1 btn-secondary">Tutup</button>
        </div>
      </form>
    </div>
  );
}